import { Box, BoxProps, Skeleton, Typography, useTheme } from '@mui/joy';
import {
	BackgroundGradient,
	BackgroundGradientProps
} from '../BackgroundGradient';
import { DotsIndicator } from '../DotsIndicator/DotsIndicator';
import { Chapter } from '../../types';
import { PropsWithChildren } from 'react';
import splatter from '../../assets/CAWP_Splatter_Paint.png';
import { BASE_URL } from '../../lib/const';

export interface ChapterPreviewContainerProps
	extends Chapter,
		PropsWithChildren,
		BoxProps {
	backgroundGradientProps?: Partial<BackgroundGradientProps>;
	disabled?: boolean;
	disabledText?: string;
	splatterBg?: boolean;
	backgroundColor?: string;
	includeLowerDots?: boolean;
	loading?: boolean;
	href?: string;
	isActive?: boolean;
	asLink?: boolean;
}

export const ChapterPreviewContainerBox = ({
	asLink,
	chapter,
	backgroundGradientProps,
	children,
	disabled,
	disabledText,
	splatterBg,
	backgroundColor,
	includeLowerDots,
	loading = false,
	isActive = true,
	href,
	...props
}: ChapterPreviewContainerProps) => {
	const theme = useTheme();
	return (
		<Box
			{...props}
			position={'relative'}
			sx={{
				cursor: asLink && href && !disabled ? 'pointer' : 'default'
			}}
		>
			<Skeleton loading={loading} position='relative' component={Box}>
				{splatterBg && (
					<img
						src={splatter}
						alt={''}
						style={{
							opacity: 0.2,
							maxHeight: '100%',
							maxWidth: '110%',
							position: 'absolute',
							zIndex: -1,
							top: '-13%',
							left: '-37%'
						}}
					/>
				)}
				<Box
					sx={{
						backgroundColor: backgroundColor
							? backgroundColor
							: theme.palette.background.surface,
						flexGrow: 1,
						height: '100%',
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
						margin: 'auto'
					}}
				>
					<Box sx={{ opacity: disabled ? 0.5 : 1 }}>
						<BackgroundGradient
							chapter={chapter}
							gradientHeight={133}
							endBackgroundColor={
								backgroundColor
									? backgroundColor
									: theme.palette.background.surface
							}
							{...backgroundGradientProps}
							sx={{
								padding: '35px',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'start',
								alignItems: 'start',
								...backgroundGradientProps?.sx
							}}
						>
							{children}
						</BackgroundGradient>
					</Box>
					<Box sx={{ flexGrow: 1 }} />
					{includeLowerDots && (
						<Box
							sx={{
								marginTop: '30px',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%'
							}}
						>
							<DotsIndicator
								chapter={chapter}
								diameter='29px'
								isActive={isActive}
							/>{' '}
							<Typography
								level='body2'
								sx={{
									marginLeft: '8px',
									textTransform: 'uppercase',
									fontSize: '0.9375rem'
								}}
							>
								{disabledText}
							</Typography>
						</Box>
					)}
				</Box>
			</Skeleton>
		</Box>
	);
};

export const ChapterPreviewContainer = ({
	asLink,
	href,
	...props
}: ChapterPreviewContainerProps) => {

	const cleansedHref = href?.includes(BASE_URL) ? href : `${BASE_URL}${href}/`

	if (asLink && !props.disabled) {
		return (
			<a
				href={cleansedHref}
				style={{
					...(props.sx as unknown as React.CSSProperties),
					textDecoration: 'none',
					display: 'flex'
				}}
			>
				<ChapterPreviewContainerBox {...props} href={href} asLink />
			</a>
		);
	}
	return <ChapterPreviewContainerBox {...props} href={href} />;
};
